import { formatInTimeZone } from "date-fns-tz";
import { Card, Icon, Tooltip, Typography } from "@/components/atoms";
import { appliedArrivalLabel, pickupDelayCaption, pickupDelayRequestPhrase, pickupReconcilesAsSum } from "@/helpers/bookingHelpers";
import { useProfile } from "@/hooks";
import { BookingDetails } from "@/redux/slices/booking/types";
import { arrivalFlightOf } from "../../../helpers/stopFlights";
import { DetailField } from "../../../common/DetailField";

interface BasicDetailsSectionProps {
  booking: BookingDetails;
}

export const BasicDetailsSection = ({ booking }: BasicDetailsSectionProps) => {
  const profile = useProfile();
  const { jobNumber, dates } = booking;

  const formatDate = (dateString: string, timezone: string, format: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Invalid date";
      }
      return formatInTimeZone(date, timezone, format);
    } catch (error) {
      return "Invalid date";
    }
  };

  // Booking Time IS the pickup, with the client's requested delay already inside it. Each row above it
  // stands on its own stored value: a booking holding one and not the other still shows the one it has.
  const delayMinutes = arrivalFlightOf(booking.stops)?.pickupDelayMinutes;
  const delayValue = pickupDelayRequestPhrase(delayMinutes);

  // The arrival the pickup was derived from, and the stored source naming what kind it was. Both come
  // off the flight object beside the delay, and both are frozen at the last write — never the live
  // position, which keeps moving while the pickup does not.
  //
  // The value arrives as UTC while every sibling time on that object is airport-local, so `formatDate`
  // converts it into the booking's timezone; rendering its wall clock verbatim would print
  // "At gate 01:57" beside "Booking Time 12:17 (Sydney)".
  //
  // The source can be absent while the arrival is present. We can then name a time but not what kind of
  // arrival it was, so no row renders: a wrong kind is worse than none.
  // The ARRIVAL flight, off the pickup stop. Both rows below describe the pickup engine's working, so
  // a DEPARTURE flight found by searching every stop would put a drop-off flight's fields behind
  // arrival-shaped labels. The departure's own display lives in `FlightSection`.
  const flight = arrivalFlightOf(booking.stops);
  const arrivalKind = appliedArrivalLabel(flight?.pickupDerivedArrivalSource);
  const arrival = flight?.pickupDerivedArrival && arrivalKind ? { iso: flight.pickupDerivedArrival, kind: arrivalKind } : null;

  // Whether the pickup still equals the arrival it was derived from plus the wait. Keyed on the HELD
  // arrival rather than the labelled one, because the two part company: an arrival stored without its
  // kind renders no row and is still perfectly checkable arithmetic. Keying on the row would assert
  // "includes the 20 min delay" on exactly the bookings where it may be false.
  //
  // Same guard as the operator bookings table and Booking History, on the same field, so one booking
  // cannot carry the claim on one surface and not another.
  const heldArrival = flight?.pickupDerivedArrival;
  const contradicted =
    !!heldArrival &&
    !pickupReconcilesAsSum({
      arrivalIso: heldArrival,
      delayMinutes,
      pickupIso: dates.pickup,
      timezone: dates.timezone,
      timeFormat: profile.timeFormat,
    });

  // The caption states in a sentence what the rows above state as a working: that the pickup already
  // has the wait folded in. It belongs to the case with no arrival row to render, where a bare Delay
  // row above a Booking Time leaves the relationship unstated and a reader could as reasonably add
  // them. With the row on screen the sentence only repeats it, and where the sum is contradicted
  // something moved the pickup after it was derived, so silence is the honest answer.
  const delayCaption = arrival || contradicted ? null : pickupDelayCaption(delayMinutes);

  return (
    <Card className="space-y-3 p-2.5">
      <DetailField leading={<Icon name="ArchiveBook" size="lg" className="text-neutral-dark-gray" />} label="Job ID" value={jobNumber} />

      <DetailField
        leading={<Icon name="Calendar" size="lg" className="text-neutral-dark-gray" />}
        label="Date"
        value={formatDate(dates.pickup, dates.timezone, "dd MMM yyyy (eee)")}
      />

      {/* Arrival → Delay → Booking Time, so where both are present the pickup reads as the sum of the two
          rows above it. The arrival is rendered in the booking's own timezone, matching that pickup. */}
      {arrival && (
        <DetailField
          leading={<Icon name="Airplane" size="lg" className="text-neutral-dark-gray" />}
          label={arrival.kind}
          value={
            <div className="flex items-center gap-2">
              <Typography variant="paragraph">{formatDate(arrival.iso, dates.timezone, profile.timeFormat)}</Typography>
            </div>
          }
        />
      )}

      {delayValue && (
        <DetailField leading={<Icon name="Timer" size="lg" className="text-neutral-dark-gray" />} label="Delay" value={delayValue} />
      )}

      <DetailField
        leading={<Icon name="Clock" size="lg" className="text-neutral-dark-gray" />}
        label="Booking Time"
        value={
          <>
            <div className="flex items-center gap-2">
              <Typography variant="paragraph">
                {formatDate(dates.pickup, dates.timezone, profile.timeFormat)} ({dates.timezone.split("/")[1]})
              </Typography>
              {profile.timezone.name !== dates.timezone && (
                <Tooltip content={formatDate(dates.pickup, profile.timezone.name, `${profile.timeFormat} zzzz`)} placement="top">
                  <Icon name="InfoCircle" className="inline-block translate-x-1 rotate-180 text-neutral-dark-gray" size="sm" />
                </Tooltip>
              )}
            </div>
            {delayCaption && (
              <Typography variant="small-regular" className="block text-neutral-dark-gray">
                {delayCaption}
              </Typography>
            )}
          </>
        }
      />
    </Card>
  );
};
